/**
 * Retailer product recommendations (PLA2-9).
 *
 * `/api/product-recommendations` returns one row per (plant genus × product ×
 * trigger type). For display we flip that around: one entry per product, with
 * the retailer's product description as the "why" and the list of design
 * plants it applies to, bucketed into Planting vs Fertilizing sections.
 */

export interface ProductRecommendation {
  genusId: string;
  genusName: string;
  productId: string;
  productName: string;
  productDescription: string;
  productImageUrl: string | null;
  triggerType: string;
  coupon: any | null;
}

export interface RecommendedProduct {
  productId: string;
  productName: string;
  /** Retailer Dashboard → Products → Description. May be empty. */
  description: string;
  imageUrl: string | null;
  triggerType: string;
  /** Display names of the design plants this product is recommended for. */
  plantNames: string[];
}

export interface RecommendedProductSection {
  triggerType: string;
  title: string;
  products: RecommendedProduct[];
}

const SECTION_ORDER = ["planting", "fertilizing"];

const SECTION_TITLES: Record<string, string> = {
  planting: "Planting Supplies",
  fertilizing: "Fertilizers",
};

function sectionTitle(triggerType: string): string {
  if (SECTION_TITLES[triggerType]) return SECTION_TITLES[triggerType];
  const words = triggerType.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Other";
}

function sectionRank(triggerType: string): number {
  const i = SECTION_ORDER.indexOf(triggerType);
  return i === -1 ? SECTION_ORDER.length : i;
}

/**
 * Group recommendation rows into one entry per product (keyed by product +
 * trigger type), sorted Planting → Fertilizing → anything else.
 *
 * @param plantNameByGenusId resolves a genus to the name shown in the design
 *   (the alias/common name the customer picked); falls back to the genus name.
 */
export function groupRecommendationsByProduct(
  recommendations: ProductRecommendation[],
  plantNameByGenusId: Record<string, string | undefined> = {}
): RecommendedProductSection[] {
  const byKey = new Map<string, RecommendedProduct>();

  for (const rec of recommendations) {
    if (!rec.productName) continue;
    const triggerType = (rec.triggerType || "planting").toLowerCase();
    const key = `${triggerType}::${rec.productId || rec.productName}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        productId: rec.productId,
        productName: rec.productName,
        description: (rec.productDescription || "").trim(),
        imageUrl: rec.productImageUrl,
        triggerType,
        plantNames: [],
      };
      byKey.set(key, entry);
    }
    const plantName = plantNameByGenusId[rec.genusId] || rec.genusName;
    if (plantName && !entry.plantNames.includes(plantName)) {
      entry.plantNames.push(plantName);
    }
  }

  const sections = new Map<string, RecommendedProductSection>();
  for (const product of byKey.values()) {
    let section = sections.get(product.triggerType);
    if (!section) {
      section = {
        triggerType: product.triggerType,
        title: sectionTitle(product.triggerType),
        products: [],
      };
      sections.set(product.triggerType, section);
    }
    section.products.push(product);
  }

  return [...sections.values()].sort(
    (a, b) =>
      sectionRank(a.triggerType) - sectionRank(b.triggerType) ||
      a.title.localeCompare(b.title)
  );
}
